import ApplicationsEmployment from './ApplicationsEmployment';
import ApplicationsNonEmploymentIncome from './ApplicationsNonEmploymentIncome';
import ApplicationsResidence from './ApplicationsResidence';
import FIMenuCustomerIdentification from '@core/classes/FIMenuCustomerIdentification';
import moment from 'moment';
import util from '@core/services/util';

export default class ApplicationsPersonalInfo {
	constructor(init) {
		this.prefix = null;
		this.firstName = null;
		this.middleName = null;
		this.lastName = null;
		this.generationSuffix = null;
		this.dateOfBirth = null;
		this.phoneNumber = null;
		this.emailAddress = null;
		this.currentResidence = new ApplicationsResidence();
		this.identifications = [];
		this.ssnType = null;
		this.ssnLast4 = null;
		this.ssnHashed = null;
		this.estimateCreditScore = null; // Possible Values: poorCredit, fairCredit, goodCredit, veryGoodCredit, excellentCredit, dontKnowCredit
		this.previousResidence = new ApplicationsResidence();
		this.employmentStatus = null; // Possible Values: employed, retired, disabled, student, unemployed, other
		this.nonEmployedStatusStartDate = null;
		this.incomeSituationDescription = null;
		this.employeeType = null; // Possible Values: w2, 1099, or anything entered by the customer
		this.currentEmployment = new ApplicationsEmployment();
		this.previousEmployment = new ApplicationsEmployment();
		this.socialSecurity = new ApplicationsNonEmploymentIncome();
		this.pension = new ApplicationsNonEmploymentIncome();
		this.supplementalSecurity = new ApplicationsNonEmploymentIncome();
		this.studentSchoolName = null;
		this.investment = new ApplicationsNonEmploymentIncome();
		this.otherIncome = new ApplicationsNonEmploymentIncome();
		this.freezeWithEquifax = null;
		this.equifaxPIN = null;
		this.freezeWithExperian = null;
		this.experianPIN = null;
		this.freezeWithTransUnion = null;
		this.transUnionPIN = null;
		this.lastSuccessfulCreditCheck = null;
		this.retirementOrDisabilityStartDate = null;

		//calculations
		this.yearsAtAddress = null;
		this.monthsAtAddress = null;
		this.mortgageRentPayment = null;
		this.yearsAtEmployment = null;
		this.monthsAtEmployment = null;
		this.yearsInNonEmployedStatus = null;
		this.monthsInNonEmployedStatus = null;
		this.monthlyGrossSalary = null;
		this.salaryType = null; //default to always Monthly

		if (init) {
			this.prefix = init.prefix;
			this.firstName = init.firstName;
			this.middleName = init.middleName;
			this.lastName = init.lastName;
			this.generationSuffix = init.generationSuffix;
			this.dateOfBirth = init.dateOfBirth ? moment(init.dateOfBirth, 'YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]').format('YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]') : init.dateOfBirth;
			this.phoneNumber = init.phoneNumber;
			this.emailAddress = init.emailAddress;
			this.currentResidence = new ApplicationsResidence(init.currentResidence);
			if (init.identifications && Array.isArray(init.identifications)) {
				this.identifications = init.identifications.map(a => new FIMenuCustomerIdentification(a));
			} else {
				this.identifications = [];
			}
			this.ssnType = init.ssnType;
			this.ssnLast4 = init.ssnLast4;
			this.ssnHashed = init.ssnHashed;
			this.estimateCreditScore = init.estimateCreditScore;
			this.previousResidence = new ApplicationsResidence(init.previousResidence);
			this.employmentStatus = init.employmentStatus;
			this.nonEmployedStatusStartDate = init.nonEmployedStatusStartDate
				? moment(init.nonEmployedStatusStartDate, 'YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]').format('YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]')
				: init.nonEmployedStatusStartDate;
			this.incomeSituationDescription = init.incomeSituationDescription;
			this.employeeType = init.employeeType;
			this.currentEmployment = new ApplicationsEmployment(init.currentEmployment);
			this.previousEmployment = new ApplicationsEmployment(init.previousEmployment);
			this.socialSecurity = new ApplicationsNonEmploymentIncome(init.socialSecurity);
			this.pension = new ApplicationsNonEmploymentIncome(init.pension);
			this.supplementalSecurity = new ApplicationsNonEmploymentIncome(init.supplementalSecurity);
			this.studentSchoolName = init.studentSchoolName;
			this.investment = new ApplicationsNonEmploymentIncome(init.investment);
			this.otherIncome = new ApplicationsNonEmploymentIncome(init.otherIncome);
			this.freezeWithEquifax = init.freezeWithEquifax;
			this.equifaxPIN = init.equifaxPIN;
			this.freezeWithExperian = init.freezeWithExperian;
			this.experianPIN = init.experianPIN;
			this.freezeWithTransUnion = init.freezeWithTransUnion;
			this.transUnionPIN = init.transUnionPIN;
			this.lastSuccessfulCreditCheck = init.lastSuccessfulCreditCheck;
			this.retirementOrDisabilityStartDate = init.retirementOrDisabilityStartDate
				? moment(init.retirementOrDisabilityStartDate, 'YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]').format('YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]')
				: init.retirementOrDisabilityStartDate;

			//calculations
			this.yearsAtAddress = init.yearsAtAddress;
			this.monthsAtAddress = init.monthsAtAddress;
			this.mortgageRentPayment = init.mortgageRentPayment;
			this.yearsAtEmployment = init.yearsAtEmployment;
			this.monthsAtEmployment = init.monthsAtEmployment;
			this.yearsInNonEmployedStatus = init.yearsInNonEmployedStatus;
			this.monthsInNonEmployedStatus = init.monthsInNonEmployedStatus;
			this.monthlyGrossSalary = init.monthlyGrossSalary;
			this.salaryType = init.salaryType;
		}
	}

	getFullName() {
		const concatenatedName = [this.prefix, this.firstName, this.middleName, this.lastName, this.suffix].filter(part => part).join(' ');
		return util.toProperCase(concatenatedName);
	}

	getDateOfBirthFormatted() {
		if (this.dateOfBirth) {
			const dob = moment(this.dateOfBirth, ['YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]', 'MM/DD/YYYY']).format('MM/DD/YYYY');
			return dob;
		}
	}

	getPhoneNumberFormatted() {
		const phone = util.formatPhoneNumber(this.phoneNumber);
		return phone;
	}

	getEmploymentStatusProperCase() {
		return util.toProperCase(this.employmentStatus);
	}

	getSalaryTypeProper() {
		return util.toProperCase(this.salaryType);
	}

	getTimeAtEmployment() {
		return `${this.yearsAtEmployment} years, ${this.monthsAtEmployment} months`;
	}

	getTimeAtNonEmployed() {
		return `${this.yearsInNonEmployedStatus} years, ${this.monthsInNonEmployedStatus} months`;
	}

	getEmploymentStatusStartDateFormatted() {
		if (this.nonEmployedStatusStartDate) {
			let movedIn = moment(this.nonEmployedStatusStartDate, ['YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]', 'MM/DD/YYYY']).format('MM/DD/YYYY');
			return movedIn;
		} else if (this.currentEmployment.startDate) {
			let movedIn = moment(this.currentEmployment.startDate, ['YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]', 'MM/DD/YYYY']).format('MM/DD/YYYY');
			return movedIn;
		}
	}

	getGrossSalaryDescription() {
		const grossSalaryDescription = [];
		if (this.employmentStatus != null) {
			//Employed Income
			if (this.employmentStatus == 'employed') {
				if (this.currentEmployment?.payAmount) {
					if (this.currentEmployment.payType == 'salary') {
						grossSalaryDescription.push('Employment Salary: $' + (this.currentEmployment.payAmount / 12).toFixed(2));
					} else {
						if (this.currentEmployment.getPaidOvertime == true && this.currentEmployment.weeklyHours > 40) {
							var overtime = (this.currentEmployment.weeklyHours - 40) * (this.currentEmployment.payAmount * 1.5);
							grossSalaryDescription.push('Employment Salary: $' + (((this.currentEmployment.payAmount * 40 + overtime) * 52) / 12).toFixed(2));
						} else {
							grossSalaryDescription.push(
								'Employment Salary: $' + ((this.currentEmployment.payAmount * this.currentEmployment.weeklyHours * 52) / 12).toFixed(2),
							);
						}
					}
				}

				//Add CommissionsOrBonuses
				if (this.currentEmployment.commissionsAndBonusesFrequency != null) {
					grossSalaryDescription.push(
						'Commissions/Bonuses Income: $' +
							this.util
								.calculateMonthlyIncome(this.currentEmployment.commissionsAndBonusesFrequency, this.currentEmployment.commissionsAndBonusesAmount)
								.toFixed(2),
					);
				}
			}
			//Retired Income
			else if (this.employmentStatus == 'retired' && this.socialSecurity != null) {
				const calculatedIncome = this.util.calculateMonthlyIncome(this.socialSecurity.frequency, this.socialSecurity.amount);

				grossSalaryDescription.push('Retirement Income: $' + (calculatedIncome ? calculatedIncome.toFixed(2) : 0));
			}
			//Disabled Income
			else if (this.employmentStatus == 'disabled' && this.supplementalSecurity != null) {
				const calculatedIncome = this.util.calculateMonthlyIncome(this.supplementalSecurity.frequency, this.supplementalSecurity.amount);

				grossSalaryDescription.push('Supplemental Security Income: $' + (calculatedIncome ? calculatedIncome.toFixed(2) : 0));
			}
		}

		//Add Pension
		if (this.pension?.frequency != null) {
			grossSalaryDescription.push('Pension Income: $' + this.util.calculateMonthlyIncome(this.pension.frequency, this.pension.amount).toFixed(2));
		}

		//Add Investments
		if (this.investment?.frequency != null) {
			grossSalaryDescription.push('Investments Income: $' + this.util.calculateMonthlyIncome(this.investment.frequency, this.investment.amount).toFixed(2));
		}

		//Add Other Income
		if (this.otherIncome?.frequency != null) {
			grossSalaryDescription.push('Other Income: $' + this.util.calculateMonthlyIncome(this.otherIncome.frequency, this.otherIncome.amount).toFixed(2));
		}

		return grossSalaryDescription;
	}

	getMonthlyGrossSalaryProper() {
		const formatted = util.formatPrice(this.monthlyGrossSalary);
		return formatted;
	}
}
